import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useSiteConfig } from "../context/SiteConfigContext";

/**
 * Shared editor hook for site_config sections.
 * Returns { cfg, set, save, saving, saved, loading }
 * @param onAfterSave optional callback (e.g. reload page)
 */
export const useConfigEditor = (onAfterSave) => {
    const { setConfig: setGlobalConfig } = useSiteConfig();
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        api.get("/config").then(({ data }) => { setCfg(data); setLoading(false); });
    }, []);

    const set = useCallback((k, v) => setCfg((c) => ({ ...c, [k]: v })), []);

    const save = useCallback(async (overrides) => {
        const payload = overrides ? { ...cfg, ...overrides } : cfg;
        setSaving(true);
        try {
            const { data } = await api.put("/config", payload);
            setCfg(data);
            setGlobalConfig?.(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            onAfterSave?.(data);
            return data;
        } finally { setSaving(false); }
    }, [cfg, setGlobalConfig, onAfterSave]);

    return { cfg, set, save, saving, saved, loading };
};

export const SaveBar = ({ onSave, saving, saved }) => (
    <div className="sticky bottom-6 z-10 mt-8" data-testid="save-bar">
        <button onClick={() => onSave()} disabled={saving}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium disabled:opacity-60 shadow-lg"
            data-testid="save-button">
            {saving ? "Saving…" : saved ? "✓ Saved!" : "Save changes"}
        </button>
    </div>
);

export const Field = ({ label, value, onChange, testid, type = "text", placeholder }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
            className="w-full px-4 py-3 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40" />
    </label>
);

export const TextArea = ({ label, value, onChange, rows = 4, testid, placeholder }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
            className="w-full px-4 py-3 rounded-sm border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y" />
    </label>
);

export const ImageInput = ({ value, onChange, testid }) => {
    const [busy, setBusy] = useState(false);
    const upload = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
        } finally { setBusy(false); }
    };
    return (
        <div className="flex items-center gap-3">
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or /api/files/…"
                className="flex-1 px-4 py-3 rounded-sm border border-border bg-background text-sm" data-testid={`${testid}-url`} />
            <label className="inline-flex items-center gap-2 px-4 py-3 rounded-sm border border-border cursor-pointer hover:border-primary/60 text-sm whitespace-nowrap">
                {busy ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`${testid}-file`} />
            </label>
        </div>
    );
};

export const Section = ({ title, description, children }) => (
    <section className="bg-card border border-border rounded-sm p-8 space-y-5">
        <div>
            <h2 className="font-display text-2xl">{title}</h2>
            {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>
        {children}
    </section>
);
